import { View, ActivityIndicator, ScrollView } from "react-native";
import MetricCard from "../components/metric-card";
import React from "react";
import StyledText from "../components/common/ui/styled-text";
import PageTemplate from "../components/common/page-template";
import { useActiveAgent } from "../contexts/ActiveAgentContext";
import { useSettingsData } from "../contexts/SettingsDataContext";
import { useDashboardStats } from "../hooks/useDashboardStats";
import { useRecentCalls } from "../hooks/useRecentCalls";
import { displayPrice } from "../utils/price-helpers";
import {
  isNonStreamOrderingAgent,
  isStreamOrderingAgent,
} from "../utils/agent-helpers";
import dayjs from "../utils/dayjs";
import { useUser } from "@clerk/clerk-react";
import CallsLast7 from "../components/calls-last-7";
import RecentInteractions from "../components/recent-interactions";
import AgentSelector from "../components/feature/header/agent-selector/agent-selector";
import UpsellCard from "../components/upsell-card";
import PaymentBanner from "../components/feature/home/payment-banner";
import {
  ArrowRight,
  BarChart2,
  Clock,
  DollarSign,
  ShoppingBag,
  Users,
} from "lucide-react-native";
import { ActionButton } from "../components/ui/action-button";
import { usePostHog } from "posthog-js/react";
import { useRouter } from "expo-router";
import { useDrawer } from "../contexts/DrawerContext";

export default function HomePage() {
  const { activeAgent, agentsLoading: isLoadingAgent } = useActiveAgent();
  const { settings } = useSettingsData();
  const posthog = usePostHog();
  const router = useRouter();
  const { setCollapsed } = useDrawer();

  const isOrdering = isStreamOrderingAgent(
    activeAgent?.type,
    settings?.posProvider === "stream"
  );
  const isNonStreamOrdering = isNonStreamOrderingAgent(
    activeAgent?.type,
    settings?.posProvider === "stream"
  );
  const { user } = useUser();

  const {
    data: stats,
    isLoading: isLoadingMetrics,
    error: statsError,
  } = useDashboardStats();
  const {
    data: recentCalls,
    isLoading: isLoadingRecentCalls,
    error: callsError,
  } = useRecentCalls();

  const emailName = user?.emailAddresses[0]?.emailAddress.split("@")[0];
  const emailNameCapitalized =
    emailName && emailName.charAt(0).toUpperCase() + emailName.slice(1);

  const handleViewMoreMetricsClick = () => {
    setCollapsed(true);
    posthog.capture("view_more_metrics_pressed", {
      agent_id: activeAgent?.id,
      agent_type: activeAgent?.type,
      is_ordering: isOrdering,
    });
    router.push("/metrics");
  };

  if (!activeAgent) {
    return null;
  }

  return (
    <PageTemplate withHeader={false}>
      <ScrollView contentContainerClassName="p-6 md:p-8 md:pt-12 lg:p-12 lg:pt-20 space-y-3 md:space-y-6 lg:space-y-10 max-w-5xl mx-auto w-full">
        <View className="flex flex-col gap-6">
          <View className="flex flex-row gap-4 items-start">
            <View className="flex-1 max-sm:mb-4">
              <View className="flex flex-col">
                <StyledText className="text-xl lg:text-2xl font-bold text-gray-900">
                  {(() => {
                    const hour = new Date().getHours();
                    if (hour >= 0 && hour < 4) return "Good night";
                    if (hour < 12) return "Good morning";
                    if (hour < 18) return "Good afternoon";
                    return "Good evening";
                  })()}
                  , {emailNameCapitalized}
                </StyledText>
                <View className="flex flex-row items-center gap-1 mt-1">
                  <Clock size={14} color="#71717A" />
                  <StyledText className="text-sm lg:text-base text-gray-600">
                    It's {dayjs().format("dddd")} {dayjs().format("h:mm A")}
                  </StyledText>
                </View>
              </View>
            </View>
            <View className="hidden lg:block">
              <AgentSelector />
            </View>
          </View>
        </View>

        {/* Payment Banner if payments feature is enabled */}
        <PaymentBanner />

        {/* Medium Screen Agent Selector */}
        <View className="hidden sm:hidden md:block lg:hidden mb-4">
          <AgentSelector />
        </View>

        {/* Mobile Recent Interactions */}
        <View className="block lg:hidden">
          <RecentInteractions
            calls={recentCalls || []}
            loading={isLoadingRecentCalls}
          />
        </View>

        {/* Metrics Grid */}
        {isLoadingAgent ? (
          <View className="h-[132px] bg-gray-50 rounded-lg flex items-center justify-center">
            <ActivityIndicator size="large" color="#6b7280" />
          </View>
        ) : (
          <View
            className={`grid gap-4 grid-cols-1 sm:grid-cols-2 ${
              isNonStreamOrdering ? "lg:grid-cols-2" : "lg:grid-cols-3"
            }`}
          >
            {isOrdering && stats?.totalRevenue != null && (
              <MetricCard
                title="Revenue"
                value={
                  stats?.totalRevenue
                    ? displayPrice(stats.totalRevenue)
                    : "$0.00"
                }
                icon={DollarSign}
                loading={isLoadingMetrics}
              />
            )}
            {isOrdering && stats?.totalOrders != null && (
              <MetricCard
                title="Orders"
                value={stats?.totalOrders?.toString() || "0"}
                icon={ShoppingBag}
                loading={isLoadingMetrics}
              />
            )}
            <MetricCard
              title="Minutes Used"
              value={stats?.totalMinutesUsed?.toString() || "0"}
              icon={Clock}
              loading={isLoadingMetrics}
            />
            {!isOrdering && (
              <>
                <MetricCard
                  title="Unique Callers"
                  value={stats?.totalUniqueCallers?.toString() || "0"}
                  icon={Users}
                  loading={isLoadingMetrics}
                />
                {!isNonStreamOrdering && <UpsellCard />}
              </>
            )}
          </View>
        )}

        {/* Charts Section */}
        <View className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <CallsLast7 data={stats?.callsLast7Days} loading={isLoadingMetrics} />

          {/* Desktop Recent Interactions */}
          <View className="hidden lg:block">
            <RecentInteractions
              calls={recentCalls || []}
              loading={isLoadingRecentCalls}
            />
          </View>
        </View>

        <View className="flex flex-col sm:flex-row gap-4">
          <ActionButton
            label="View More Metrics"
            onPress={handleViewMoreMetricsClick}
            icon={BarChart2}
            iconRight={ArrowRight}
            variant="outline"
            className="flex-1 hover:bg-gray-50"
          />
        </View>
      </ScrollView>
    </PageTemplate>
  );
}
